import {Box, Card, CardMedia, CircularProgress} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import DirectionsBoatIcon from '@material-ui/icons/DirectionsBoat';
import {PSingleLine} from 'components/TypographyShortcuts';
import clsx from 'clsx';
import Overlay from 'components/Overlay';
import {Subtitle} from 'components/TypographyShortcuts';
import useIsCg719s from 'components/forms/hooks/useIsCg719s';
import {useGetVessel} from 'hooks/useGetEndpoint';
import {useState} from 'react';
import LoadingPage from 'components/LoadingPage';
import {CloudErrorPage} from 'components/LoadingPage';
import {PlainVesselModal} from './VesselModal';
import {toSnakeCase} from 'components/forms/locale/utils';
import isObject from 'isobject';

const selectedShadow =
    '0 4px 8px 0 rgba(71, 132, 176, 1), 0 6px 20px 0 rgba(71, 132, 176, 0.9)';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        height: 100,
        width: '100%',
        maxWidth: 500,
        overflow: 'hidden',
    },
    hoverRootBackground: {
        '&:hover': {
            backgroundColor:
                theme.palette.type === 'dark'
                    ? theme.palette.gray.light2
                    : theme.palette.primary.light4,
        },
    },
    details: {
        padding: '0.5em',
        width: '100%',
    },
    picture: {
        width: 151,
        display: 'flex',
        backgroundColor: theme.palette.gray.light,
    },
}));

const NoVessel = () => {
    const classes = useStyles();
    return (
        <Card
            className={classes.root}
            style={{borderStyle: 'dashed', alignItems: 'center'}}
        >
            <Box
                className={classes.details}
                style={{padding: '2em', textAlign: 'center'}}
            >
                <PSingleLine>No Vessel Chosen Yet</PSingleLine>
                <PSingleLine>
                    Please select a vessel below or choose "I Don't See My Boat"
                </PSingleLine>
            </Box>
        </Card>
    );
};

const VesselLoading = () => {
    const classes = useStyles();
    return (
        <Card
            className={classes.root}
            style={{borderStyle: 'dashed', alignItems: 'center'}}
        >
            <Box
                className={classes.details}
                style={{
                    padding: '2em',
                    textAlign: 'center',
                    display: 'inline-flex',
                }}
            >
                <span style={{margin: 'auto'}}>Fetching vessel data... </span>
                <CircularProgress />
            </Box>
        </Card>
    );
};

const NewVessel = ({edit}) => {
    const classes = useStyles();
    return (
        <Card className={classes.root} style={{borderStyle: 'solid'}}>
            <Box
                className={classes.details}
                style={{padding: '1em', textAlign: 'center'}}
            >
                <PSingleLine>{edit ? 'Edit' : 'New'} Vessel</PSingleLine>
                <PSingleLine>
                    {edit
                        ? 'Please click "next" to continue editing your vessel.'
                        : 'Please click "next" at the bottom to enter the vessel information.'}
                </PSingleLine>
            </Box>
        </Card>
    );
};

const VesselCard = ({
    vessel,
    selectedId,
    handleDetailVesselUpdate = () => {},
    context = '',
}) => {
    const classes = useStyles();
    const isCg719s = useIsCg719s();
    const boatTooBig = isCg719s && vessel?.tonnage > 200;
    const displayTonnage =
        vessel.tonnage_type === 'gt'
            ? vessel?._gt_eq
            : vessel?.tonnage_type === 'grt'
            ? vessel?.tonnage
            : vessel?.tonnage;

    return (
        <Card
            className={clsx(
                classes.root,
                !boatTooBig && classes.hoverRootBackground
            )}
            onClick={boatTooBig ? () => {} : e => handleDetailVesselUpdate(vessel)}
            style={{
                boxShadow:
                    selectedId === vessel?.mmseas_uuid ? selectedShadow : null,
            }}
            data-cy={`vessel-card-${toSnakeCase(vessel?.vessel_name)}${
                context ? `-${context}` : ''
            }`}
        >
            <CardMedia className={classes.picture}>
                <DirectionsBoatIcon
                    style={{
                        fontSize: 60,
                        color: '#424242',
                        margin: 'auto',
                    }}
                />
            </CardMedia>
            <Box className={classes.details}>
                <PSingleLine align='left'>{vessel?.vessel_name}</PSingleLine>
                <Subtitle align='right'>
                    {vessel?.length_tonnage} ft, {parseInt(displayTonnage * 10) / 10}{' '}
                    {vessel?.tonnage_type || 'grt'}
                </Subtitle>
                <Subtitle align='right'>
                    {vessel?.engine_horsepower
                        ? `${vessel.engine_horsepower} hp,`
                        : null}{' '}
                    {vessel?.vessel_type}
                </Subtitle>
                {vessel?.organization_name ? (
                    <Subtitle align='right'>{vessel.organization_name}</Subtitle>
                ) : null}
                {vessel?.service ? (
                    <Subtitle align='right'>{vessel.service}</Subtitle>
                ) : null}
            </Box>
        </Card>
    );
};

const StandAloneVesselCard = ({vesselId, vesselData}) => {
    // for use anywhere in the site. just provide a vesselId and api will retreive data
    const {data: vesselModel, isLoading, error} = useGetVessel(vesselId);
    const classes = useStyles();
    const [openModal, setOpenModal] = useState(false);
    const vessel =
        isObject(vesselModel) && Object.keys(vesselModel).length
            ? vesselModel
            : vesselData;

    return [
        <Card
            className={clsx(classes.root, classes.hoverRootBackground)}
            onClick={() => setOpenModal(true)}
            key='card'
            data-cy={`vessel-card-${toSnakeCase(vessel?.vessel_name)}`}
        >
            {isLoading ? (
                <LoadingPage />
            ) : error ? (
                <CloudErrorPage error={error} />
            ) : vessel ? (
                [
                    <CardMedia className={classes.picture} key='media'>
                        <DirectionsBoatIcon
                            style={{
                                fontSize: 60,
                                color: '#424242',
                                margin: 'auto',
                            }}
                        />
                    </CardMedia>,
                    <Box className={classes.details} key='details'>
                        <PSingleLine align='left'>{vessel?.vessel_name}</PSingleLine>
                        <Subtitle align='right'>
                            {vessel?.length_tonnage} ft, {vessel?.tonnage} grt
                        </Subtitle>
                        <Subtitle align='right'>
                            {vessel?.engine_horsepower
                                ? `${vessel.engine_horsepower} hp,`
                                : null}{' '}
                            {vessel?.vessel_type}
                        </Subtitle>
                        {vessel?.organization_name ? (
                            <Subtitle align='right'>
                                {vessel.organization_name}
                            </Subtitle>
                        ) : null}
                        {vessel?.service ? (
                            <Subtitle align='right'>{vessel.service}</Subtitle>
                        ) : null}
                    </Box>,
                ]
            ) : null}
        </Card>,
        <PlainVesselModal
            vessel={vessel}
            open={openModal}
            setOpen={setOpenModal}
            key='modal'
        />,
    ];
};

const VesselCardWithOverlay = ({vessel, innerRef, ...props}) => {
    const isCg719s = useIsCg719s();
    const boatTooBig = isCg719s && vessel?.tonnage > 200;

    return (
        <Overlay
            condition={boatTooBig}
            overlay={
                <PSingleLine style={{margin: '2em', backgroundColor: 'white'}}>
                    Cannot use boats over 200 tons for Cg719s form
                </PSingleLine>
            }
            innerRef={innerRef}
        >
            <VesselCard {...props} vessel={vessel} />
        </Overlay>
    );
};

export default VesselCardWithOverlay;
export {
    VesselCard,
    VesselLoading,
    NoVessel,
    NewVessel,
    selectedShadow,
    StandAloneVesselCard,
};
